/**
 * Typed bindings for /api/company/settings — one blob of company-wide
 * preferences (Tier 1/2/3). GET returns a fully-defaulted object; PATCH takes
 * a partial (per-group) and deep-merges server-side.
 *
 * The telegram bot token is write-only: GET returns it blank with
 * `telegram_bot_token_set` telling whether one is stored.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface GeneralSettings {
  timezone: string;
}
export type WarmupSpeed = "natural" | "fast" | "aggressive" | "auto";
export interface WarmupSettings {
  auto_start_on_import: boolean;
  drip_start_window_hours: number;
  economy_mode: "auto" | "on" | "off";
  max_joins_per_day: number | null;
  max_reactions_per_day: number | null;
}
export interface ImportSettings {
  default_warmup_speed: WarmupSpeed;
  default_source_label: string | null;
}
export interface InviterSettings {
  default_strategy: "manual_add" | "dm_message" | "channel_forward" | "story_mention";
  outreach_quiet_hours_enabled: boolean;
  outreach_active_hour_start: number;
  outreach_active_hour_end: number;
  campaign_sends_per_hour: number;
}
export interface ProxySettings {
  auto_health_check: boolean;
  health_check_interval_hours: number;
  one_account_per_proxy: boolean;
}
export interface InboxSettings {
  poll_interval_minutes: number;
}
export interface NotificationSettings {
  channel: "none" | "telegram";
  telegram_chat_id: string | null;
  telegram_bot_token: string;
  telegram_bot_token_set: boolean;
  on_account_banned: boolean;
  on_proxy_dead: boolean;
  on_campaign_done: boolean;
  on_quota_exhausted: boolean;
}
export interface SecuritySettings {
  enforce_2fa: boolean;
}
export interface DataSettings {
  auto_delete_archived_dialogs_days: number | null;
}
export interface ConversationSettings {
  enabled: boolean;
  max_partners_per_account: number;
  turn_gap_hours_min: number;
  turn_gap_hours_max: number;
  include_warming: boolean;
}

/** «Свои группы и каналы»: a channel the panel makes gets a second admin. */
export interface OwnChatsSettings {
  second_admin: boolean;
  second_admin_after_minutes: number;
  /** null — the panel picks the account. */
  second_admin_account_id: string | null;
}

export interface CompanySettings {
  general: GeneralSettings;
  warmup: WarmupSettings;
  importing: ImportSettings;
  inviter: InviterSettings;
  proxy: ProxySettings;
  inbox: InboxSettings;
  notifications: NotificationSettings;
  security: SecuritySettings;
  data: DataSettings;
  conversations: ConversationSettings;
  own_chats: OwnChatsSettings;
}

export type CompanySettingsPatch = {
  [K in keyof CompanySettings]?: Partial<CompanySettings[K]>;
};

const KEY = ["company_settings"] as const;

export function useCompanySettings() {
  return useQuery<CompanySettings>({
    queryKey: KEY,
    queryFn: () => api.get<CompanySettings>("/api/company/settings"),
    staleTime: 60_000,
  });
}

export function useUpdateCompanySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: CompanySettingsPatch) =>
      api.patch<CompanySettings>("/api/company/settings", patch),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}
